import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { MergeCellsOutlined, EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import PatientModal from "./PatientModal";
import MergePatientModal from "./MergePatientModal";
import QuickCreatePatientModal from "./QuickCreatePatientModal";
import { fetchPatients, fetchPatient, mergePatients, MergeAmendedSearchRequest } from "../../../api/healthcare/PatientApi";
import { classifyMergeFailure, isMergedWithPartialFailure, MergeAttemptResult, parseIhiReviewOutcomes } from "./mergeOutcome";
import {
	PATIENT_LIST_SCOPE_WITH_CUSTOMERS,
	Patient,
} from "../../../types/healthcare/PatientTypes";
import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";
import { useNotifications } from "../../../context/NotificationContext";
import { isUserUuid } from "../../../utils/auth/userUuid";

export type MergeDirection = "up" | "down";



const PatientManagementPage: React.FC = () => {
	const [patients, setPatients] = useState<Patient[]>([]);
	const [totalPatients, setTotalPatients] = useState<number>(0);
	const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
	const [selectedPatients, setSelectedPatients] = useState<Patient[]>([]);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [showDeactivated, setShowDeactivated] = useState<boolean>(false);

	const [sortField, setSortField] = useState<string>("created_at");
	const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | undefined>("descend");
	const [searchTerm, setSearchTerm] = useState<string>("");

	const PATIENTS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	const { showError } = useNotifications();

	const [isMerging, setIsMerging] = useState<boolean>(false);
	const [mergeModalOpen, setMergeModalOpen] = useState<boolean>(false);
	const [mergeDirection, setMergeDirection] = useState<MergeDirection>("up");

	const [quickCreateOpen, setQuickCreateOpen] = useState<boolean>(false);

	const [searchParams, setSearchParams] = useSearchParams();

	// Debounce handle for the search box. Same 300ms pattern as MessagingPanel and
	// CreateTreatmentPlanModal — deliberately NOT pushed down into PageToolbar, whose
	// per-keystroke onChange contract every other page table relies on.
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const loadRequestIdRef = useRef(0);

	useEffect(() => {
		setIsLoading(true);
		loadPatients();
	}, [sortField, sortOrder, showDeactivated]);

	// Drop any in-flight debounce so an unmounted page never fires a request.
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		};
	}, []);

	// Deep-link: when navigated to /healthcare/patients?id={pid}, open that patient's
	// modal via the same path a table-row click uses, then clear the param so reopening
	// is predictable.
	useEffect(() => {
		const idParam = searchParams.get("id")?.toLowerCase();
		if (!idParam) return;
		if (isUserUuid(idParam)) {
			handleEditClick({ id: idParam } as Patient);
		}
		setSearchParams({}, { replace: true });
	}, [searchParams]);

	const handlePageChange = (page: number) => {
		// `undefined`, not `""`: the term is read from state inside loadPatients, so
		// paging keeps whatever the operator searched for.
		loadPatients(undefined, page);
	};

	/**
	 * `search` is an explicit OVERRIDE, not the transport for the term. Every caller
	 * that simply wants "reload with whatever is currently searched" omits it and the
	 * current `searchTerm` is read from state. Only handleSearch (which fires from a
	 * debounce, before its own setState has landed) and handleReset (which deliberately
	 * clears the filter) pass a value.
	 */
	const loadPatients = async (
		search?: string,
		page?: number,
	): Promise<{ data: Patient[]; total: number } | null> => {
		const requestId = ++loadRequestIdRef.current;
		const term = search ?? searchTerm;

		setIsLoading(true);

		try {
			const sortDirection = sortOrder === "ascend" ? "asc" : "desc";
			const request = term.trim()
				? fetchPatients(
					page ?? currentPage,
					PATIENTS_PER_PAGE,
					term,
					sortField,
					sortDirection,
					!showDeactivated,
					{ listScope: PATIENT_LIST_SCOPE_WITH_CUSTOMERS },
				)
				: fetchPatients(
					page ?? currentPage,
					PATIENTS_PER_PAGE,
					term,
					sortField,
					sortDirection,
					!showDeactivated,
				);

			const { data, total } = await request;

			if (requestId !== loadRequestIdRef.current) return null;

			setPatients(data);
			setTotalPatients(total);
			setCurrentPage(page ?? currentPage);

			return { data, total };
		} catch (err) {
			if (requestId !== loadRequestIdRef.current) return null;

			console.error("Error fetching patients:", err);
			showError("Failed to load patients.");

			return null;
		} finally {
			if (requestId === loadRequestIdRef.current) {
				setIsLoading(false);
			}
		}
	};

	const handleSearch = (value: string) => {
		setSearchTerm(value);
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		searchTimeoutRef.current = setTimeout(() => loadPatients(value, 1), 300);
	};

	const handleReset = () => {
		// Cancel a pending debounce first, or the term the operator just cleared would
		// be re-applied ~300ms after the list came back.
		if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
		setSearchTerm("");
		setShowDeactivated(false);
		loadPatients("", 1);
	};

	const onNewPatient = () => {
		setQuickCreateOpen(true);
	};

	const handleEditClick = async (patient: Patient) => {
		try {
			setIsLoading(true);
			const { data: freshPatient } = await fetchPatient(String(patient.id));

			setSelectedPatient(freshPatient);
			setIsModalOpen(true);
		} catch (error) {
			console.error("Error loading patient/customer record:", error);
			showError("Failed to load record details.");
		} finally {
			setIsLoading(false);
		}
	};

	const handleModalClose = async () => {
		setSelectedPatient(null);
		setIsModalOpen(false);
		await loadPatients(searchTerm, currentPage);
	};

	const handlePatientUpdated = (updated: Patient) => {
		setSelectedPatient(updated);
		setPatients((prev) =>
			prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
		);
	};


	const onMergePatients = () => {
		if (selectedPatients.length === 2) {
			setMergeModalOpen(true);
		}
	};

	/** Triggers the merge API call and reports whether it started, returned a merge outcome, or was handled here. */
	const confirmMergePatients = async (amendedSearch?: MergeAmendedSearchRequest): Promise<MergeAttemptResult> => {
		setIsMerging(true);
		try {
			let primary: Patient;
			let secondary: Patient;
			if (mergeDirection === "up") {
				primary = selectedPatients[1];
				secondary = selectedPatients[0];
			} else {
				primary = selectedPatients[0];
				secondary = selectedPatients[1];
			}
			const response = await mergePatients(primary.id, secondary.id, amendedSearch);
			if (response.success || isMergedWithPartialFailure(response)) {
				// CU-14ygtkjc8yy: the POST body is the only carrier of the operator's HI review outcomes; the status poll never has them.
				const ihiReviews = parseIhiReviewOutcomes(response.ihi_review_outcomes);
				return response.merge_id != null
					? { kind: "started", mergeId: response.merge_id, ...(ihiReviews.length > 0 ? { ihiReviews } : {}) }
					: { kind: "handled" };
			} else {
				showError(response.message || "Failed to merge patients.");
				return { kind: "handled" };
			}
		} catch (error) {
			// CU-14ygtkj9tu0: the request error carries the amended criteria, so it is classified and never logged.
			const outcome = classifyMergeFailure(error, amendedSearch !== undefined);
			if (outcome) {
				return { kind: "outcome", outcome };
			}
			showError("Failed to merge patients.");
			return { kind: "handled" };
		} finally {
			setIsMerging(false);
		}
	};

	/** Called when the merge modal is closed (cancel or after viewing results). */
	const handleMergeModalClose = async () => {
		setMergeModalOpen(false);
		await loadPatients();
	};

	const columns: ColumnsType<Patient> = [

		{
			title: "Given Name",
			dataIndex: "first_name",
			key: "first_name",
			sorter: true,
			sortOrder: sortField === "first_name" ? sortOrder : undefined,
		},
		{
			title: "Family Name",
			dataIndex: "last_name",
			key: "last_name",
			sorter: true,
			sortOrder: sortField === "last_name" ? sortOrder : undefined,
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			sorter: true,
			sortOrder: sortField === "email" ? sortOrder : undefined,
			ellipsis: true,
		},
		{
			title: "Date Of Birth",
			dataIndex: "date_of_birth",
			key: "date_of_birth",
			render: (d: string) => (d ? format(new Date(d), "dd MMM yyyy") : "—"),
		},
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			width: 120,
			sorter: true,
			sortOrder: sortField === "created_at" ? sortOrder : undefined,
			render: (d: string) => (d ? format(new Date(d), "dd MMM yyyy") : "—"),
		},
	];

	return (
		<Layout>
			<PageContainer data-testid="patients-root">
				<PageToolbar
					primaryAction={{ label: "Create Patient", onClick: onNewPatient, testId: "patients-create-btn" }}
					extraActions={[
						...(selectedPatients.length === 2
							? [{ label: "Merge", icon: <MergeCellsOutlined />, onClick: onMergePatients }]
							: []),
						{
							label: showDeactivated ? "Hide Deactivated" : "Show Deactivated",
							icon: <EyeOutlined />,
							onClick: () => setShowDeactivated((v) => !v),
						},
					]}
					filters={[
						{
							type: "search",
							placeholder: "Search name, email or phone…",
							value: searchTerm,
							onChange: handleSearch,
							width: 280,
						},
					]}
					onReset={handleReset}
				/>

				<DataTable
					columns={columns}
					dataSource={patients}
					rowKey="id"
					loading={isLoading}
					onRowClick={handleEditClick}
					total={totalPatients}
					currentPage={currentPage}
					pageSize={PATIENTS_PER_PAGE}
					onPageChange={handlePageChange}
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={(field, order) => {
						setSortField(field);
						setSortOrder(order);
						setCurrentPage(1);
					}}
					selectedRows={selectedPatients}
					setSelectedRows={setSelectedPatients}
					emptyTitle="No patients found"
					emptyDescription="Create your first patient to get started"
					emptyAction={{ label: "Create Patient", onClick: onNewPatient }}
					fillHeight
				/>

				{selectedPatient && (
					<PatientModal
						isOpen={isModalOpen}
						onClose={handleModalClose}
						patient={selectedPatient}
						onPatientUpdated={handlePatientUpdated}
					/>
				)}

				<QuickCreatePatientModal
					open={quickCreateOpen}
					onClose={() => setQuickCreateOpen(false)}
					onCreated={() => loadPatients()}
					onReviewCreatedPatient={(created) => { void handleEditClick(created); }}
				/>

				{mergeModalOpen && (
					<MergePatientModal
						modalOpen={mergeModalOpen}
						confirmMerge={confirmMergePatients}
						selectedPatients={selectedPatients}
						cancelMerge={handleMergeModalClose}
						mergeDirection={mergeDirection}
						setMergeDirection={setMergeDirection}
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default PatientManagementPage;